import React, { useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  TablePagination,
} from "@mui/material";
import { tokens } from "../../theme";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AgGridHeader from "components/agGridHeader";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";

ModuleRegistry.registerModules([AllCommunityModule]);

export default function AgGridListing(props) {
  const { rowData = [], setShowCompany } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [gridApi, setGridApi] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const columnDefs = useMemo(() => {
    const booleanRenderer = (params) => {
      return params.value ? (
        <CheckIcon style={{ color: colors.grey[200] }} fontSize="small" />
      ) : (
        <CloseIcon style={{ color: colors.grey[300] }} fontSize="small" />
      );
    };

    return [
      {
        headerName: "Company Name",
        field: "name",
        flex: 1,
        floatingFilter: false,
        tooltipValueGetter: (params) => params.value,
      },
      {
        headerName: "ERP ID",
        field: "code",
        width: 180,
        floatingFilter: false,
        tooltipValueGetter: (params) => params.value,
      },
      {
        headerName: "Client Id",
        field: "irbclientid",
        flex: 1,
        floatingFilter: false,
        tooltipValueGetter: (params) => params.value,
        valueGetter: (params) => params.data?.irbclientid || "-",
      },
      {
        headerName: "Client Secret",
        field: "irbsecret",
        flex: 1,
        floatingFilter: false,
        tooltipValueGetter: (params) => params.value,
        valueGetter: (params) => params.data?.irbsecret || "-",
      },
      {
        headerName: "Tin Number",
        field: "tin_number",
        flex: 1,
        floatingFilter: false,
        tooltipValueGetter: (params) => params.value,
        valueGetter: (params) => params.data?.tin_number || "-",
      },
      {
        headerName: "Email",
        field: "emailreceiver",
        flex: 1,
        floatingFilter: false,
        tooltipValueGetter: (params) => params.value,
        valueGetter: (params) => params.data?.emailreceiver || "-",
      },
      {
        headerName: "Invoice Option",
        field: "invnumsource",
        flex: 1,
        floatingFilter: false,
        tooltipValueGetter: (params) => params.value,
        valueGetter: (params) => params.data?.invnumsource || "-",
      },
      {
        headerName: "Intermediary",
        field: "intermediary",
        width: 130,
        floatingFilter: false,
        cellRenderer: booleanRenderer,
      },
      {
        headerName: "Active",
        field: "isActive",
        width: 90,
        floatingFilter: false,
        cellRenderer: booleanRenderer,
      },
      {
        headerName: "Action",
        field: "actions",
        width: 80,
        filter: false,
        sortable: false,
        floatingFilter: false,
        cellRenderer: (params) => {
          if (!params.data) return null;
          return (
            <IconButton color="primary" size="small" onClick={() => setShowCompany(params.data.id)}>
              <Tooltip title="Edit Company" placement="right">
                <BorderColorIcon fontSize="small" />
              </Tooltip>
            </IconButton>
          );
        },
      },
    ];
  }, [colors, setShowCompany]);

  // Same defaultColDef as Receivable — allows moving, pinning, resizing
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      suppressMenu: true,
      headerComponent: AgGridHeader,
      headerComponentParams: {
        suppressPinning: true,
      },
    };
  }, []);

  const onGridReady = (params) => {
    setGridApi(params.api);
    params.api.setGridOption("paginationPageSize", pageSize);
  };

  const onPaginationChanged = (event) => {
    const api = event.api;
    if (api) {
      const gridPage = api.paginationGetCurrentPage();
      const gridPageSize = api.paginationGetPageSize();
      setPage((prev) => (gridPage !== prev ? gridPage : prev));
      setPageSize((prev) => (gridPageSize !== prev ? gridPageSize : prev));
    }
  };

  const handlePageChange = (event, newPage) => {
    if (gridApi) gridApi.paginationGoToPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    if (gridApi) {
      gridApi.setGridOption("paginationPageSize", newSize);
      gridApi.paginationGoToPage(0);
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        className="ag-theme-material"
        style={{ height: "calc(100% - 52px)", width: "100%" }}
        sx={{
          "& .ag-root-wrapper": {
            border: "none",
            backgroundColor: `${colors.grey[800]} !important`,
            fontFamily: "inherit",
          },
          "& .ag-header": {
            backgroundColor: `${colors.grey[600]} !important`,
            borderBottom: "none",
          },
          "& .ag-header-row": { borderBottom: "none" },
          "& .ag-header-cell": {
            borderRight: "1px solid transparent !important",
            backgroundColor: `${colors.grey[600]} !important`,
            transition: "border-color 0.15s ease-in-out",
          },
          "& .ag-header:hover .ag-header-cell": {
            borderRight: `1px solid ${colors.grey[700]} !important`,
          },
          "& .ag-header-cell-label": {
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          },
          "& .ag-header-cell-text": {
            color: `${colors.primary[100]} !important`,
            fontWeight: "bold",
            fontSize: "13px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: "1 1 auto",
          },
          "& .ag-header-cell-label .ag-icon": {
            fontSize: "11px !important",
            marginLeft: "4px !important",
            flex: "0 0 auto",
            opacity: "0.8",
          },
          "& .ag-header-cell .ag-icon-filter": {
            display: "none !important",
          },
          "& .ag-header-cell:hover .ag-icon-filter, & .ag-header-cell.ag-header-cell-filtered .ag-icon-filter": {
            display: "inline-block !important",
          },
          "& .ag-pinned-left-header": {
            backgroundColor: `${colors.grey[600]} !important`,
            borderRight: `1px solid ${colors.grey[700]}`,
          },
          "& .ag-floating-filter-button": { display: "none !important" },
          "& .ag-row": {
            borderBottom: `1px solid ${colors.grey[700]} !important`,
            backgroundColor: `${colors.grey[800]} !important`,
            color: `${colors.grey[100]} !important`,
          },
          "& .ag-row-hover": { backgroundColor: `${colors.grey[700]} !important` },
          "& .ag-cell": {
            borderRight: "none",
            display: "flex",
            alignItems: "center",
            fontSize: "13px",
            overflow: "hidden",
          },
          "& .ag-cell-value": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
          },
          "& .ag-pinned-left-cols-container": {
            backgroundColor: `${colors.grey[800]} !important`,
            borderRight: `1px solid ${colors.grey[700]}`,
          },
          "& .ag-header-cell-resize::after": {
            backgroundColor: `${colors.primary[100]} !important`,
            width: "1.5px !important",
            height: "60% !important",
            top: "20% !important",
            opacity: "0.15 !important",
            transition: "opacity 0.15s ease-in-out, background-color 0.15s ease-in-out, width 0.15s ease-in-out",
          },
          "& .ag-header-cell:hover .ag-header-cell-resize::after": {
            opacity: "0.7 !important",
            width: "2px !important",
          },
          "& .ag-header-cell-resize:hover::after": {
            opacity: "1 !important",
            backgroundColor: `${colors.greenAccent?.[500] || "#4caf50"} !important`,
            width: "3px !important",
            height: "80% !important",
            top: "10% !important",
          },
        }}
      >
        <AgGridReact
          theme="legacy"
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={pageSize}
          suppressPaginationPanel={true}
          suppressCellFocus={true}
          enableCellTextSelection={true}
          rowHeight={32}
          headerHeight={36}
          floatingFiltersHeight={36}
        />
      </Box>

      <TablePagination
        component="div"
        count={rowData.length}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[100, 250, 500]}
        sx={{
          backgroundColor: colors.grey[600],
          color: `${colors.primary[100]} !important`,
          borderTop: `1px solid ${colors.grey[700]}`,
          borderRadius: "0 0 4px 4px",
          "& .MuiTablePagination-actions .MuiIconButton-root": {
            color: `${colors.primary[100]} !important`,
          },
          "& .MuiTablePagination-selectIcon": {
            color: `${colors.primary[100]} !important`,
          },
        }}
      />
    </div>
  );
}
